import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Formik } from 'formik';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';

import discountCodesClient from '../../../../clients/discount-codes';
import TextInput from '../../../../web-app-components/components/TextInput';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import useDialog from '../../../../web-app-components/hooks/useDialog';
import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 400,
  },
  submit: {
    marginTop: theme.spacing(1),
    alignSelf: 'flex-start',
  },
  readOnly: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
}));

const validate = (values) => {
  const errors = {};
  if (!(values.Code && values.Code.trim())) {
    errors.Code = 'Kodas privalomas';
  }
  if (values.DiscountPercent != null) {
    const p = Number(values.DiscountPercent);
    if (Number.isNaN(p) || p < 1 || p > 100) {
      errors.DiscountPercent = 'Nuolaida turi būti nuo 1 iki 100';
    }
  }
  if (values.UsageLimit != null) {
    const n = Number(values.UsageLimit);
    if (Number.isNaN(n) || n < 1 || !Number.isInteger(n)) {
      errors.UsageLimit =
        'Panaudojimų limitas turi būti sveikasis skaičius ≥ 1';
    }
  }
  return errors;
};

const ExistingDiscount = ({ id }) => {
  const classes = useStyles();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirmRemoveDialog = useDialog();

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const item = await discountCodesClient.findOne(id);
        setData(item?.data ?? item);
      } catch (error) {
        enqueueSnackbar('Nepavyko gauti nuolaidos kodo', { variant: 'error' });
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, enqueueSnackbar]);

  const handleSubmit = async (values) => {
    try {
      await discountCodesClient.update(id, {
        Code: values.Code.trim(),
        DiscountPercent: Number(values.DiscountPercent),
        Target: 'routes',
        UsageLimit: Number(values.UsageLimit),
        Active: values.Active !== false,
      });
      enqueueSnackbar('Nuolaidos kodas atnaujintas', { variant: 'success' });
      setData((prev) => (prev ? { ...prev, ...values } : null));
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        'Nepavyko atnaujinti';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleRemoveClick = () => confirmRemoveDialog.setOpen(true);

  const handleRemoveConfirm = async () => {
    try {
      await discountCodesClient.remove(id);
      enqueueSnackbar('Nuolaidos kodas pašalintas', { variant: 'success' });
      const listPath = path.replace(/\/[^/]+$/, '');
      history.push(listPath);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        'Nepavyko pašalinti';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return (
      <Box>
        <Typography color="error">Nuolaidos kodas nerastas</Typography>
      </Box>
    );
  }

  const initialValues = {
    Code: data.Code ?? '',
    DiscountPercent: data.DiscountPercent ?? 10,
    UsageLimit: data.UsageLimit ?? 100,
    Active: data.Active !== false,
  };

  return (
    <div>
      <Box className={classes.header}>
        <Typography variant="h5">Redaguoti nuolaidos kodą</Typography>
        <ButtonSlim onClick={handleRemoveClick} color="secondary">
          Pašalinti
        </ButtonSlim>
      </Box>
      <Typography className={classes.readOnly}>
        Panaudota: {data.UsageCount ?? 0} / {data.UsageLimit ?? 0}
      </Typography>
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: submit,
          isSubmitting,
        }) => (
          <form onSubmit={submit} className={classes.form}>
            <TextInput
              name="Code"
              label="Kodas"
              value={values.Code}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.Code && Boolean(errors.Code)}
              helperText={touched.Code && errors.Code}
              fullWidth
            />
            <TextInput
              name="DiscountPercent"
              label="Nuolaida (%)"
              type="number"
              inputProps={{ min: 1, max: 100 }}
              value={values.DiscountPercent}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.DiscountPercent && Boolean(errors.DiscountPercent)}
              helperText={touched.DiscountPercent && errors.DiscountPercent}
              fullWidth
            />
            <TextInput
              name="UsageLimit"
              label="Panaudojimų limitas"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={values.UsageLimit}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.UsageLimit && Boolean(errors.UsageLimit)}
              helperText={touched.UsageLimit && errors.UsageLimit}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="Active"
                  checked={values.Active}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="Aktyvus"
            />
            <Box className={classes.submit}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Išsaugoma...' : 'Išsaugoti'}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
      <ConfirmRemoveDialog
        {...confirmRemoveDialog}
        onClose={() => confirmRemoveDialog.setOpen(false)}
        onConfirm={handleRemoveConfirm}
      />
    </div>
  );
};

ExistingDiscount.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ExistingDiscount;
